import { navigate } from 'sly-svelte-location-router'
import { clearAuth } from '@lib/store/auth'
import { withCache } from '@lib/cache'
import type {
  User,
  Device,
  DeviceLog,
  DeviceTag,
  DeviceAction,
  GitHubRelease,
  UserSettings,
  SearchResult,
  MqttEvent,
  ApiMessageResponse,
  ApiCreateUserResponse,
  ApiLoginResponse,
  ApiRefreshTokenResponse,
  ApiDeviceTokenResponse,
  ApiDeviceActionsResponse,
  ApiStatusResponse,
  ApiVersionResponse,
  ApiDeviceVersionsCountResponse,
  ApiTokenExchangeResponse,
  SDMRegisterResponse
} from '@types'

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: any
  headers?: Record<string, string>
  skipAuthRedirect?: boolean
  cache?: boolean
  cacheTTL?: number
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

let isRedirecting = false
const activeRequests = new Map<string, AbortController>()

export function cancelAllRequests() {
  for (const controller of activeRequests.values()) {
    controller.abort()
  }
  activeRequests.clear()
}

async function handleUnauthorized() {
  if (isRedirecting) return

  isRedirecting = true

  console.log('[API] Unauthorized - clearing auth and redirecting to login')

  cancelAllRequests()

  try {
    clearAuth()
  } catch (err) {
    console.error('Failed to clear auth state:', err)
  }

  navigate('/login', {
    state: { message: 'Session expired. Please login again.' },
    replace: true
  })

  setTimeout(() => {
    isRedirecting = false
  }, 1000)
}

export async function api<T = any>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuthRedirect = false, cache = false, cacheTTL = 10 * 60 * 1000 } = options

  if (cache) {
    const cacheKey = `api:${method}:${endpoint}`
    return withCache(cacheKey, () => apiInternal<T>(endpoint, options), cacheTTL)
  }

  return apiInternal<T>(endpoint, options)
}

async function apiInternal<T = any>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuthRedirect = false } = options

  const controller = new AbortController()
  const requestKey = `${method}:${endpoint}`
  activeRequests.set(requestKey, controller)

  const config: RequestInit = {
    method,
    credentials: 'include',
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  }

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(endpoint, config)
  } catch (err) {
    activeRequests.delete(requestKey)
    throw new ApiError(0, `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`)
  }

  if (response.status === 401 && !skipAuthRedirect) {
    await handleUnauthorized()
    throw new ApiError(401, 'Unauthorized - Please log in again')
  }

  if (!response.ok) {
    let userMessage = 'An error occurred'

    switch (response.status) {
      case 400:
        userMessage = 'Invalid request'
        break
      case 403:
        userMessage = 'Access denied'
        break
      case 404:
        userMessage = 'Resource not found'
        break
      case 500:
        userMessage = 'Server error'
        break
      default:
        userMessage = 'Request failed'
    }

    if (response.status !== 404 || !endpoint.includes('/api/device/')) {
      console.error(`API Error: ${response.status} ${response.statusText}`)
    }
    throw new ApiError(response.status, userMessage)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    activeRequests.delete(requestKey)
    return {} as T
  }

  try {
    return await response.json()
  } finally {
    activeRequests.delete(requestKey)
  }
}

export const apiGet = <T = any>(endpoint: string, options?: Omit<ApiOptions, 'method'>) => 
  api<T>(endpoint, { method: 'GET', ...options })

export const apiPost = <T = any>(endpoint: string, body?: any, options?: Omit<ApiOptions, 'method' | 'body'>) => 
  api<T>(endpoint, { method: 'POST', body, ...options })

export const apiPut = <T = any>(endpoint: string, body?: any, options?: Omit<ApiOptions, 'method' | 'body'>) => 
  api<T>(endpoint, { method: 'PUT', body, ...options })

export const apiPatch = <T = any>(endpoint: string, body?: any, options?: Omit<ApiOptions, 'method' | 'body'>) => 
  api<T>(endpoint, { method: 'PATCH', body, ...options })

export const apiDelete = <T = any>(endpoint: string, options?: Omit<ApiOptions, 'method'>) =>
  api<T>(endpoint, { method: 'DELETE', ...options })

export async function apiDownload(endpoint: string, filename: string): Promise<void> {
  const config: RequestInit = {
    method: 'GET',
    credentials: 'include',
  }

  let response: Response
  try {
    response = await fetch(endpoint, config)
  } catch (err) {
    throw new ApiError(0, `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`)
  }

  if (response.status === 401) {
    await handleUnauthorized()
    throw new ApiError(401, 'Unauthorized - Please log in again')
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'Download failed')
  }

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const get_devices = async (params?: { limit?: number; offset?: number; query?: string; sortBy?: string; sortOrder?: string }) => {
  const { deviceStore } = await import('@lib/store/devices')
  const { get } = await import('svelte/store')

  const queryHash = JSON.stringify(params)
  const currentStore = get(deviceStore)

  if (currentStore.hash !== queryHash) {
    deviceStore.set({ isLoading: true, hash: queryHash, devices: [] })
  } else if (currentStore.isLoading) {
    return
  } else {
    deviceStore.update(s => ({ ...s, isLoading: true }))
  }

  const queryParams = new URLSearchParams()
  if (params?.limit) queryParams.set('limit', params.limit.toString())
  if (params?.offset) queryParams.set('offset', params.offset.toString())
  if (params?.query) queryParams.set('query', params.query)
  if (params?.sortBy) queryParams.set('sortBy', params.sortBy)
  if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder)

  try {
    const devices = await apiGet<Device[]>(`/api/device?${queryParams.toString()}`)
    deviceStore.set({ isLoading: false, hash: queryHash, devices })
  } catch (err) {
    console.error('Failed to get devices:', err)
    deviceStore.update(s => ({ ...s, isLoading: false }))
  }
}

export const get_device = (id: string) =>
  apiGet<Device>(`/api/device/${id}`)

export const getDeviceActions = (id: string) =>
  apiGet<ApiDeviceActionsResponse>(`/api/device/${id}/action`)

export const deviceAction = (id: string, action: { action: string; [key: string]: any }) =>
  apiPost<void>(`/api/device/${id}/action/${action.action}`, action)

export const get_device_logs = (id: string, params?: { cursor?: string; from?: string; to?: string }) => {
  const queryParams = new URLSearchParams()
  if (params?.cursor) queryParams.set('cursor', params.cursor)
  if (params?.from) queryParams.set('from', params.from)
  if (params?.to) queryParams.set('to', params.to)
  return apiGet<DeviceLog[]>(`/api/device/${id}/logs?${queryParams.toString()}`)
}

export const deleteDevice = (id: string) =>
  apiDelete<void>(`/api/device/${id}`)

export const getDeviceConnections = (id: string, limit = 20, offset = 0) =>
  apiGet<MqttEvent[]>(`/api/device/${id}/connections?limit=${limit}&offset=${offset}`)

export const getServerConnections = (limit = 20, offset = 0) =>
  apiGet<MqttEvent[]>(`/api/status/connections?limit=${limit}&offset=${offset}`)

export const getDeviceVersionCount = (id: string) =>
  apiGet<ApiDeviceVersionsCountResponse>(`/api/device/${id}/version/count`)

export const generateDeviceToken = () =>
  apiGet<ApiDeviceTokenResponse>('/sdm/token')

export const sdmRegisterDevice = (data: any) =>
  apiPost<SDMRegisterResponse>('/sdm/register', data)

export const listApkReleases = (latest?: boolean) =>
  apiGet<GitHubRelease[]>(`/api/apk/releases${latest ? '?latest=true' : ''}`)

export const listCliReleases = (latest?: boolean) =>
  apiGet<GitHubRelease[]>(`/api/cli/releases${latest ? '?latest=true' : ''}`)

export const getCliReleaseDownloadUrl = (tag: string) =>
  `/api/cli/releases/${encodeURIComponent(tag)}`

export const searchDeviceTags = () =>
  apiGet<DeviceTag[]>('/api/search/device')

export const search = (query: string) =>
  apiGet<SearchResult[]>(`/api/search?query=${encodeURIComponent(query)}`)

export const getVersion = () =>
  apiGet<ApiVersionResponse>('/api/status/version')

export const checkLiveness = () =>
  apiGet<void>('/api/status/health/liveness')

export const checkReadiness = () =>
  apiGet<void>('/api/status/health/readiness')